/**
 * The origin that backend image assets should be fetched from.
 *
 * NEXT_PUBLIC_IMAGE_BASE_URL, when set, wins for both variants — it is the
 * local-dev escape hatch that points image loads at the live server (where the
 * uploaded files actually live) instead of the local backend (which only has
 * the database, not the files). It is a NEXT_PUBLIC_ var so Next inlines it
 * into the client bundle, and it resolves identically during SSR and after
 * hydration. Leave it unset in production and the pre-existing hosts below are
 * used, so behavior there is unchanged.
 *
 * With no override:
 *  - "optimized": the target of a /_next/image optimizer fetch, which the Next
 *    *server* performs — so it targets the Docker-internal host (mirrored to
 *    the client via NEXT_PUBLIC_INTERNAL_API_BASE_URL). See
 *    resolveOptimizedImageUrl for the full rationale.
 *  - "public": the src of a plain `<img>` the *browser* fetches, so it targets
 *    the public host (NEXT_PUBLIC_BASE_URL). See resolveImageUrl.
 */
function resolveBackendImageBase(variant: "optimized" | "public"): string {
  const override = process.env.NEXT_PUBLIC_IMAGE_BASE_URL?.replace(/\/$/, "");
  if (override) {
    return override;
  }

  if (variant === "public") {
    return process.env.NEXT_PUBLIC_BASE_URL?.replace(/\/$/, "") || "";
  }

  // NEXT_PUBLIC_INTERNAL_API_BASE_URL is listed first because it is the only
  // one of these three that Next inlines into the *client* bundle. This
  // function runs in client components (TeacherAvatar, AvatarImage) whose trees
  // render in the browser, where the server-only INTERNAL_API_BASE_URL is
  // `undefined` — without the public mirror the client would silently fall
  // through to the public host, which the Docker-internal optimizer fetch
  // cannot reach (a 500 from /_next/image). Exposing the internal host to the
  // browser is safe: it only ever becomes the `url=` param of the same-origin
  // /_next/image request, which the Next *server* dereferences — the browser
  // never resolves this host itself.
  return (
    process.env.NEXT_PUBLIC_INTERNAL_API_BASE_URL ||
    process.env.INTERNAL_API_BASE_URL ||
    process.env.NEXT_PUBLIC_BASE_URL ||
    ""
  ).replace(/\/$/, "");
}

/**
 * Resolves a potentially relative image path to a full URL using the backend base URL.
 *
 * Targets the public host (NEXT_PUBLIC_BASE_URL), never the server-only
 * INTERNAL_API_BASE_URL — this URL ends up in a plain `<img src>`/`srcset`
 * (or a `next/image` `<Image unoptimized>`, which renders the same way),
 * fetched directly by the *browser* regardless of whether this function ran
 * during SSR or on the client. A Docker-internal host like `laravel.test`
 * would be unreachable from the visitor's browser.
 *
 * Local-dev override: NEXT_PUBLIC_IMAGE_BASE_URL (see resolveBackendImageBase)
 * takes over as the target host when set, and a URL the backend baked with its
 * own public host is rebased onto it — so plain `<img>` loads also come from
 * the live server. Unset in production, where both behaviors are no-ops.
 *
 * Do NOT use this for an optimized (non-`unoptimized`) `next/image` `<Image>`
 * — use resolveOptimizedImageUrl() instead, see its doc comment for why.
 */
export function resolveImageUrl(path: string | null | undefined): string | null {
  if (!path) {
    return null;
  }

  if (path.startsWith("data:")) {
    return path;
  }

  const baseUrl = resolveBackendImageBase("public");

  // Rebase a URL the backend baked with its own public host (e.g.
  // http://localhost:8000/storage/...) onto the image origin, so the
  // NEXT_PUBLIC_IMAGE_BASE_URL override reaches these too. With no override
  // baseUrl *is* the public host, so this is an identity no-op.
  const publicBase = process.env.NEXT_PUBLIC_BASE_URL?.replace(/\/$/, "");
  if (publicBase && path.startsWith(publicBase)) {
    return `${baseUrl}${path.slice(publicBase.length)}`;
  }

  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  return `${baseUrl}${normalizedPath}`;
}

/**
 * Resolves a backend image path/URL for use as an *optimized* next/image
 * (`<Image>` without `unoptimized`) src.
 *
 * Unlike resolveImageUrl(), this never targets NEXT_PUBLIC_BASE_URL and never
 * branches on `typeof window`. next/image's built-in optimizer (`/_next/image`)
 * always fetches the source image itself, from the Next.js *server* process —
 * regardless of whether the React tree that referenced the image rendered
 * during SSR or after client hydration. The browser only ever requests the
 * same-origin `/_next/image?url=...` proxy path; it never resolves the raw
 * backend host directly. So:
 *
 *   1. A relative path is prefixed with the internal host
 *      (NEXT_PUBLIC_INTERNAL_API_BASE_URL / INTERNAL_API_BASE_URL, falling
 *      back to NEXT_PUBLIC_BASE_URL), not the public host.
 *   2. An already-absolute URL the backend baked in using its own public
 *      APP_URL (e.g. `http://localhost:8000/storage/...`, via Laravel's
 *      `asset()` helper — this is how site-settings/CMS image fields like
 *      about-us and how-to-join-ijaazah steps arrive) is rebased onto the
 *      internal host, since that public host is exactly as unreachable from
 *      the Next.js container as it is from any other Docker service.
 *   3. Any other absolute URL (a third-party CDN, unsplash, etc.) is left
 *      untouched.
 *
 * The internal host must therefore be visible on BOTH sides. Branching on
 * `typeof window` would be actively wrong (the SSR HTML and the client's
 * hydration pass would disagree — a hydration mismatch — and the client value
 * would use the public host the Docker optimizer can't reach). But simply
 * reading a server-only env var is not enough either: in a client component
 * INTERNAL_API_BASE_URL is `undefined`, so the client silently fell through to
 * the public host and every browser-rendered avatar 500'd. Hence the
 * NEXT_PUBLIC_INTERNAL_API_BASE_URL mirror, which Next inlines client-side.
 *
 * next.config.mjs's `images.remotePatterns` must allow the internal host for
 * the rebased/prefixed URL to pass next/image's validation.
 *
 * Local-dev override: when NEXT_PUBLIC_IMAGE_BASE_URL is set it replaces the
 * internal host as the fetch target for backend images (see
 * resolveBackendImageBase). Uploaded files only exist on the live server, so
 * pointing this at the live host lets a local checkout load them without
 * copying the files down. Left unset in production, so behavior is unchanged.
 *
 * Only applies to `<Image>` usages that don't pass `unoptimized` — an
 * `unoptimized` `<Image>` renders a plain `<img>` fetched directly by the
 * browser, so it must use resolveImageUrl() (NEXT_PUBLIC_BASE_URL) instead.
 */
export function resolveOptimizedImageUrl(path: string | null | undefined): string | null {
  if (!path) {
    return null;
  }

  if (path.startsWith("data:")) {
    return path;
  }

  const targetBase = resolveBackendImageBase("optimized");

  const publicBase = process.env.NEXT_PUBLIC_BASE_URL?.replace(/\/$/, "");
  if (publicBase && path.startsWith(publicBase)) {
    return `${targetBase}${path.slice(publicBase.length)}`;
  }

  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${targetBase}${normalizedPath}`;
}
